import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { SOLAR_INSTALLER_MIN_SAMPLE } from '../../domains/narrative/solarCostStatistics';

interface SolarPVInstallersCostData {
  chartData: any;
  chartOptions: any;
  narrative?: string;
  totalPairs?: number;
  topPair?: string;
  topPairCost?: number;
  avgCost?: number;
  lowestCostInstaller?: string;
  highestCostInstaller?: string;
  lowestInstallerCostPerW?: number | null;
  highestInstallerCostPerW?: number | null;
  qualifyingPairCount?: number;
  comparisonUniverseLabel?: string;
  lowestInstallerN?: number | null;
  highestInstallerN?: number | null;
  coverageUnavailable?: boolean;
}

// Helper function to get geofips for a location
async function getGeofipsForLocation(locationName: string): Promise<number | null> {
  try {
    return await supabaseLocationService.getGeofips(locationName);
  } catch (error) {
    console.error('Error getting geofips for location:', locationName, error);
    return null;
  }
}

export default async function fetchSolarPVInstallersCostSupabase(location: string): Promise<SolarPVInstallersCostData> {
  try {
    // Get geofips for the location
    const geofips = await getGeofipsForLocation(location);
    
    if (!geofips) {
      return {
        coverageUnavailable: true,
        chartData: { labels: [], datasets: [] },
        chartOptions: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: `Top Solar PV Installer-Manufacturer Pairs (Cost) - ${location} (No Geofips Found)`,
              font: { size: 16, weight: 'bold' as const }
            },
            legend: {
              display: false
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Installer-Manufacturer Pair'
              }
            },
            y: {
              title: {
                display: true,
                text: 'Cost per Watt ($/W)'
              },
              beginAtZero: true
            }
          }
        },
        narrative: `No geofips found for ${location}.`,
        totalPairs: undefined,
        topPair: undefined,
        topPairCost: undefined,
        avgCost: undefined,
      };
    }

    // Query Supabase by geoid for server-side filtering
    let { data: installerData, error } = await supabaseClient
      .from('pv_top10_installers_by_location')
      .select('*')
      .eq('geoid', geofips)
      .order('total_kw', { ascending: false });

    if (error) {
      console.error('Error fetching Solar PV Installers data:', error);
      throw error;
    }

    // Fallback: if no rows by geoid, try location_name (case-insensitive)
    if (!installerData || installerData.length === 0) {
      const { data: byLocation, error: locErr } = await supabaseClient
        .from('pv_top10_installers_by_location')
        .select('*')
        .ilike('location_name', `%${location}%`)
        .order('total_kw', { ascending: false });
      if (locErr) {
        console.error('Error fetching Solar PV Installers by location_name:', locErr);
      } else if (byLocation && byLocation.length > 0) {
        installerData = byLocation;
      }
    }

    if (!installerData || installerData.length === 0) {
      return {
        coverageUnavailable: true,
        chartData: { labels: [], datasets: [] },
        chartOptions: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: `Installer aggregate reported cost — ${location} (no data)`,
            },
          },
        },
        narrative: `No installer cost records for ${location}.`,
        totalPairs: undefined,
        topPair: undefined,
        topPairCost: undefined,
        avgCost: undefined,
      };
    }

    type InstallerLocationRow = (typeof installerData)[number];
    const mapPair = (item: InstallerLocationRow) => {
      const totalKw = Number(item.total_kw) || 0;
      const totalCost = Number(item.total_cost) || 0;
      const totalProjects = Number(item.total_projects) || 0;
      const costPerWatt =
        totalKw > 0 && totalCost > 0 ? totalCost / (totalKw * 1000) : null;
      return {
        installer: item.installer || 'Unknown',
        manufacturer: item.manufacturer || 'Unknown',
        totalKw,
        totalCost,
        totalProjects,
        costPerWatt,
      };
    };

    const allPairs = installerData.map(mapPair);
    const qualifyingAll = allPairs.filter(
      (item) =>
        item.costPerWatt != null &&
        item.costPerWatt > 0 &&
        item.totalKw > 0 &&
        item.totalProjects >= SOLAR_INSTALLER_MIN_SAMPLE
    );
    const sortedByCost = [...qualifyingAll].sort(
      (a, b) => (a.costPerWatt ?? 0) - (b.costPerWatt ?? 0)
    );

    const processedData = sortedByCost.slice(0, 10);

    // Combine installer and manufacturer: "Installer (Manufacturer)"
    const labels = processedData.map(item => {
      const installer = item.installer || 'Unknown';
      const manufacturer = item.manufacturer || 'Unknown';
      return manufacturer !== 'Unknown' && manufacturer !== installer 
        ? `${installer} (${manufacturer})`
        : installer;
    });
    if (!qualifyingAll.length) {
      return {
        coverageUnavailable: true,
        chartData: { labels: [], datasets: [] },
        chartOptions: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: `Installer aggregate reported cost — ${location} (no qualifying pairs)`,
            },
          },
        },
        narrative: `No installer pairs with at least ${SOLAR_INSTALLER_MIN_SAMPLE} applications for ${location}.`,
      };
    }

    const data = processedData.map((item) => item.costPerWatt as number);

    // Create color array for bars
    const colors = [
      'rgba(59, 130, 246, 0.8)',   // Blue
      'rgba(16, 185, 129, 0.8)',   // Green
      'rgba(245, 158, 11, 0.8)',   // Amber
      'rgba(239, 68, 68, 0.8)',    // Red
      'rgba(139, 92, 246, 0.8)',   // Purple
      'rgba(6, 182, 212, 0.8)',    // Cyan
      'rgba(251, 146, 60, 0.8)',   // Orange
      'rgba(34, 197, 94, 0.8)',    // Green
      'rgba(168, 85, 247, 0.8)',   // Purple
      'rgba(236, 72, 153, 0.8)'    // Pink
    ];

    const chartData = {
      labels: labels,
      datasets: [{
        label: 'Cost per Watt',
        data: data,
        backgroundColor: colors.slice(0, labels.length),
        borderColor: colors.slice(0, labels.length).map(c => c.replace('0.8', '1')),
        borderWidth: 1
      }]
    };

    const chartOptions = {
      type: 'bar' as const,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: `Top Solar PV Installer-Manufacturer Pairs (Cost) - ${location}`,
          font: { size: 18, weight: 'bold' as const },
          padding: { top: 10, bottom: 20 }
        },
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: function(context: any) {
              const index = context.dataIndex;
              const item = processedData[index];
              return [
                `Installer: ${item.installer}`,
                `Manufacturer: ${item.manufacturer}`,
                `Cost per Watt: $${item.costPerWatt != null ? item.costPerWatt.toFixed(2) : '—'}/W`,
                `Total kW: ${item.totalKw.toFixed(1)} kW`,
                `Total Cost: $${item.totalCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
                `Total Projects: ${item.totalProjects.toLocaleString()}`
              ].filter(Boolean);
            }
          }
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Installer-Manufacturer Pair',
            font: { size: 14, weight: 'bold' as const }
          },
          ticks: {
            maxRotation: 45,
            minRotation: 45,
            font: { size: 12 }
          }
        },
        y: {
          title: {
            display: true,
            text: 'Cost per Watt ($/W)',
            font: { size: 14, weight: 'bold' as const }
          },
          ticks: {
            font: { size: 12 }
          },
          beginAtZero: true
        }
      }
    };

    // Calculate summary metrics
    const pairLabel = (item: { installer: string; manufacturer: string }) =>
      item.manufacturer !== 'Unknown' && item.manufacturer !== item.installer
        ? `${item.installer} (${item.manufacturer})`
        : item.installer;

    const qualifyingPairCount = qualifyingAll.length;
    const lowest = sortedByCost[0];
    const highest = sortedByCost[sortedByCost.length - 1];
    const lowestCostInstaller = lowest ? pairLabel(lowest) : '—';
    const highestCostInstaller =
      qualifyingPairCount >= 2 && highest ? pairLabel(highest) : '—';
    const lowestInstallerCostPerW = lowest ? lowest.costPerWatt : null;
    const highestInstallerCostPerW =
      qualifyingPairCount >= 2 && highest ? highest.costPerWatt : null;

    const narrative = `${location}: aggregate reported cost ($/W DC) for installer–manufacturer pairs with at least ${SOLAR_INSTALLER_MIN_SAMPLE} applications.`;

    return {
      chartData,
      chartOptions,
      narrative,
      totalPairs: qualifyingPairCount,
      topPair: lowestCostInstaller,
      topPairCost: lowestInstallerCostPerW ?? undefined,
      avgCost: undefined,
      lowestCostInstaller,
      highestCostInstaller,
      lowestInstallerCostPerW,
      highestInstallerCostPerW,
      qualifyingPairCount,
      comparisonUniverseLabel:
        'qualifying installer-manufacturer pairs within the DGStats top-10-by-installed-capacity rollup',
      lowestInstallerN: lowest?.totalProjects ?? null,
      highestInstallerN: qualifyingPairCount >= 2 ? highest?.totalProjects ?? null : null,
      coverageUnavailable: false,
    };

  } catch (error) {
    console.error('Error in fetchSolarPVInstallersCostSupabase:', error);
    throw error;
  }
}

